import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  FileText,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  Trash2,
  Shield,
  Eye,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const KYCVerification = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadKycStatus = useCallback(async () => {
    try {
      const response = await api.get('/kyc/status');
      setKycStatus(response.data);
    } catch (error) {
      console.error('Error loading KYC status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKycStatus();
  }, [loadKycStatus]);

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Fichier trop volumineux (max 10MB)');
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PDF, JPG ou PNG.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    formData.append('document_name', file.name);

    try {
      await api.post('/kyc/upload-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploadé');
      loadKycStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await api.delete(`/kyc/document/${documentId}`);
      toast.success('Document supprimé');
      loadKycStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSubmitKYC = async () => {
    setSubmitting(true);
    try {
      await api.post('/kyc/submit');
      toast.success('Dossier soumis pour vérification');
      loadKycStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Approuvé</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Refusé</Badge>;
      case 'submitted':
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />En cours de vérification</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3 mr-1" />En attente</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">Non démarré</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  // If verified by agent
  if (kycStatus?.verified_by_agent) {
    return (
      <div className="section-padding py-6">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Compte vérifié</h2>
            <p className="text-slate-500">
              Votre compte a été vérifié par un agent terrain lors de votre inscription.
            </p>
            <Badge className="mt-4 bg-green-100 text-green-700">
              <CheckCircle className="w-4 h-4 mr-1" />
              Vérifié par agent
            </Badge>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If approved
  if (kycStatus?.status === 'approved') {
    return (
      <div className="section-padding py-6">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Vérification approuvée</h2>
            <p className="text-slate-500">
              Votre dossier KYC a été vérifié et approuvé. Votre compte est maintenant pleinement actif.
            </p>
            <p className="text-xs text-slate-400 mt-4">
              Vérifié le {new Date(kycStatus.verified_at).toLocaleDateString('fr-FR')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const requiredDocs = kycStatus?.required_documents || [];
  const uploadedDocs = kycStatus?.documents || [];
  const uploadedTypes = uploadedDocs.map(d => d.type);
  const progress = requiredDocs.length > 0 
    ? Math.round((uploadedDocs.filter(d => requiredDocs.some(r => r.type === d.type && r.required)).length / requiredDocs.filter(r => r.required).length) * 100)
    : 0;

  return (
    <div className="section-padding py-6" data-testid="kyc-verification-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Vérification du compte
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Uploadez vos documents pour activer pleinement votre compte
          </p>
        </div>

        {/* Status Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  kycStatus?.status === 'rejected' ? 'bg-red-100' :
                  kycStatus?.status === 'submitted' ? 'bg-blue-100' :
                  'bg-amber-100'
                }`}>
                  <Shield className={`w-6 h-6 ${
                    kycStatus?.status === 'rejected' ? 'text-red-600' :
                    kycStatus?.status === 'submitted' ? 'text-blue-600' :
                    'text-amber-600'
                  }`} />
                </div>
                <div>
                  <h3 className="font-semibold">Statut de vérification</h3>
                  {getStatusBadge(kycStatus?.status)}
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">{progress}%</p>
                <p className="text-xs text-slate-500">Complété</p>
              </div>
            </div>
            <Progress value={progress} className="h-2" />

            {/* Rejection reason */}
            {kycStatus?.status === 'rejected' && kycStatus?.rejection_reason && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">Motif du refus :</p>
                <p className="text-sm text-red-700 mt-1">{kycStatus.rejection_reason}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documents */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Documents requis</CardTitle>
            <CardDescription>
              Formats acceptés : PDF, JPG, PNG (max 10MB)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {requiredDocs.map((doc) => {
              const uploaded = uploadedDocs.find(d => d.type === doc.type);
              
              return (
                <div 
                  key={doc.type}
                  className={`p-4 rounded-lg border-2 ${
                    uploaded ? 'border-green-200 bg-green-50' : 'border-dashed border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <FileText className={`w-5 h-5 ${uploaded ? 'text-green-600' : 'text-slate-400'}`} />
                        <h4 className="font-medium">
                          {doc.label}
                          {doc.required && <span className="text-red-500 ml-1">*</span>}
                        </h4>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{doc.description}</p>

                      {uploaded && (
                        <div className="flex items-center gap-2 mt-3">
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {uploaded.name}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const fileUrl = uploaded.url;
                              const isPDF = fileUrl && (fileUrl.toLowerCase().includes('.pdf') || uploaded.name?.toLowerCase().endsWith('.pdf'));
                              if (isPDF) {
                                // Use Google Docs Viewer for PDFs
                                window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}`, '_blank');
                              } else {
                                window.open(fileUrl, '_blank');
                              }
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {kycStatus?.status !== 'submitted' && kycStatus?.status !== 'approved' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500"
                              onClick={() => handleDeleteDocument(uploaded.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {!uploaded && kycStatus?.status !== 'submitted' && (
                      <div>
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          id={`file-${doc.type}`}
                          onChange={(e) => handleFileUpload(doc.type, e.target.files[0])}
                          disabled={uploading}
                        />
                        <Label
                          htmlFor={`file-${doc.type}`}
                          className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          {uploading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                          Uploader
                        </Label>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Submit Button */}
            {kycStatus?.status !== 'submitted' && kycStatus?.status !== 'approved' && (
              <Button
                className="w-full mt-6 bg-green-500 hover:bg-green-600"
                onClick={handleSubmitKYC}
                disabled={submitting || uploadedDocs.length === 0}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Soumettre pour vérification
              </Button>
            )}

            {kycStatus?.status === 'submitted' && (
              <div className="text-center py-6">
                <Clock className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">Dossier en cours de vérification</p>
                <p className="text-sm text-slate-500 mt-1">
                  Vous recevrez un email une fois la vérification terminée.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KYCVerification;
